import { useContext } from 'react';

import type { ContextValue } from '../../components/SunriseHoldoutExperimentContextProvider';
import { SunriseHoldoutExperimentContext } from '../../components/SunriseHoldoutExperimentContextProvider';

export type EnabledState = {
	enabled: boolean;
};

export type HighlightTextProps = {
	selected: boolean;
};

export type HighlightTextState = {
	useAccentStyles: boolean;
};

export type IsSunriseEnabledProps = {
	isSunriseEnabled: boolean;
};

export type IsSunriseEnabledState = IsSunriseEnabledProps & EnabledState;

export type MembershipFormState = IsSunriseEnabledProps & {
	noSelected: boolean;
	yesSelected: boolean;
	onSelectNo: () => void;
	onSelectYes: () => void;
	acceptMembership: boolean;
	setUserInteracted: ContextValue['setUserInteracted'];
	noSelectionMadeError: ContextValue['noSelectionMadeError'];
	onSubmit: MembershipFormProps['onSubmit'];
};

export type MembershipFormProps = IsSunriseEnabledProps & {
	onSelectNo: () => void;
	onSelectYes: () => void;
	acceptMembership: boolean;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
};

export type SelectionProps = IsSunriseEnabledProps & {
	checked: boolean;
};

export type SelectionState = IsSunriseEnabledProps & {
	shouldHideRadioButtons: boolean;
	shouldShowSelectionBorder: boolean;
};

export type ValidatePayPalProps = {
	hasPayPalValidationErrors: boolean;
};

export type ValidatePayPalState = {
	validatePayPal: () => boolean;
	isPayPalButtonEnabled: boolean;
};

type Props = HighlightTextProps | IsSunriseEnabledProps | MembershipFormProps | SelectionProps | ValidatePayPalProps;

export function useSunriseHoldoutExperiment(): EnabledState;
export function useSunriseHoldoutExperiment(props: HighlightTextProps): HighlightTextState;
export function useSunriseHoldoutExperiment(props: IsSunriseEnabledProps): IsSunriseEnabledState;
export function useSunriseHoldoutExperiment(props: MembershipFormProps): MembershipFormState;
export function useSunriseHoldoutExperiment(props: SelectionProps): SelectionState;
export function useSunriseHoldoutExperiment(props: ValidatePayPalProps): ValidatePayPalState;
// eslint-disable-next-line max-lines-per-function
export function useSunriseHoldoutExperiment(
	props?: Props,
):
	| EnabledState
	| HighlightTextState
	| IsSunriseEnabledState
	| MembershipFormState
	| SelectionState
	| ValidatePayPalState {
	const { setUserInteracted, userInteracted, variant, noSelectionMadeError, setNoSelectionMadeError } = useContext(
		SunriseHoldoutExperimentContext,
	);
	const enabled = isEnabledVariant(variant);

	if (!props) return { enabled };

	if (isHighlightTextState(props)) return highlightTextState(props.selected, variant);
	// CHANGE-76340 for this variant_2 experiment, user interaction is required to enable the PayPal button, if displayed
	const needsUserInteraction = variant === 'variant_2' && !userInteracted;

	if (isValidatePayPalState(props))
		return validatePayPalState({ ...props, needsUserInteraction, setNoSelectionMadeError });

	const isSunriseEnabled = enabled || props.isSunriseEnabled;

	if (isSelectionState(props)) return selectionState(isSunriseEnabled, props.checked, variant);

	const isMembershipFormState = 'acceptMembership' in props;
	if (!isMembershipFormState) {
		const isSunriseEnabledStateReturnValue: IsSunriseEnabledState = {
			enabled,
			isSunriseEnabled,
		};

		return isSunriseEnabledStateReturnValue;
	}

	const yesSelected = needsUserInteraction ? false : props.acceptMembership;
	const noSelected = needsUserInteraction ? false : !props.acceptMembership;

	function handleSelection(callback: MembershipFormProps['onSelectNo'] | MembershipFormProps['onSelectYes']) {
		return () => {
			setUserInteracted(true);
			setNoSelectionMadeError(false);
			callback();
		};
	}

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		if (!yesSelected && !noSelected && variant === 'variant_2') {
			e.preventDefault();
			setNoSelectionMadeError(true);

			return;
		}

		await props.onSubmit(e);
	};

	const membershipFormStateReturnValue: MembershipFormState = {
		isSunriseEnabled,
		yesSelected,
		noSelected,
		onSelectNo: handleSelection(props.onSelectNo),
		onSelectYes: handleSelection(props.onSelectYes),
		acceptMembership: yesSelected,
		setUserInteracted,
		noSelectionMadeError: enabled && noSelectionMadeError,
		onSubmit: handleSubmit,
	};

	return membershipFormStateReturnValue;
}

function isHighlightTextState(props: Props): props is HighlightTextProps {
	return 'selected' in props;
}

function highlightTextState(selected: boolean, variant: string | undefined): HighlightTextState {
	return { useAccentStyles: selected || (isEnabledVariant(variant) && variant !== 'control') };
}

function isSelectionState(props: Props): props is SelectionProps {
	return 'checked' in props;
}

function selectionState(isSunriseEnabled: boolean, checked: boolean, variant: string | undefined): SelectionState {
	return {
		isSunriseEnabled,
		shouldHideRadioButtons: variant !== 'variant_1',
		shouldShowSelectionBorder: variant === 'variant_2' && !checked,
	};
}

function isEnabledVariant(variant: string | undefined) {
	return !!variant && ['control', 'variant_1', 'variant_2'].includes(variant);
}

function isValidatePayPalState(props: Props): props is ValidatePayPalProps {
	return 'hasPayPalValidationErrors' in props;
}

function validatePayPalState(
	props: ValidatePayPalProps & {
		needsUserInteraction: boolean;
		setNoSelectionMadeError: ContextValue['setNoSelectionMadeError'];
	},
): ValidatePayPalState {
	return {
		validatePayPal: () => {
			if (props.needsUserInteraction) {
				props.setNoSelectionMadeError(true);
				return false;
			}
			if (props.hasPayPalValidationErrors) {
				return false;
			}
			return true;
		},
		isPayPalButtonEnabled: !props.needsUserInteraction,
	};
}
